import { type INumberType, type ITargetCountry } from 'entities/Compliance/api/types/complianceType'

/**
 * Which compliance areas an organization must complete. Extracted from
 * CompliancePageStore so the Compliance page menu and `get_compliance_status`
 * cannot disagree; the page store keeps only the Sinch UK filter, which needs
 * live entity state.
 */
export type IComplianceTab = 'BP' | 'LN' | 'TF' | 'Calling' | 'UK'

export type IComplianceTier = 'required' | 'recommended' | 'optional' | 'international' | 'none'

export interface IComplianceTierInput {
  targetCountry: ITargetCountry | null
  hasTaxId: boolean | null
  numbersType: INumberType | null
  isUKCountry: boolean
  /** Toll-free numbers bought but not yet verified: they must be dealt with. */
  hasUnverifiedTollFree: boolean
}

export type IComplianceTiers = Record<IComplianceTab, IComplianceTier>

const NONE: IComplianceTiers = {
  BP: 'none',
  LN: 'none',
  TF: 'none',
  Calling: 'none',
  UK: 'none',
}

const tiers = (over: Partial<IComplianceTiers>): IComplianceTiers => ({
  ...NONE,
  UK: 'international',
  ...over,
})

const LOCAL_FIRST = tiers({
  BP: 'required',
  LN: 'required',
  TF: 'optional',
  Calling: 'recommended',
})

const TOLL_FREE_FIRST = tiers({
  TF: 'required',
  BP: 'recommended',
  LN: 'optional',
  Calling: 'recommended',
})

const ALL_REQUIRED = tiers({
  BP: 'required',
  LN: 'required',
  TF: 'required',
  Calling: 'recommended',
})

const base = ({
  targetCountry,
  hasTaxId,
  numbersType,
  isUKCountry,
}: IComplianceTierInput): IComplianceTiers => {
  if (targetCountry !== 'CA') {
    if (numbersType === 'local') return LOCAL_FIRST
    if (numbersType === 'toll_free') return TOLL_FREE_FIRST
    if (numbersType === 'all' || hasTaxId) return ALL_REQUIRED
    if (isUKCountry) return tiers({ BP: 'required' })
    // Unanswered questionnaire: no areas at all, not even the international tab.
    return NONE
  }

  // Canada registers toll-free rather than 10DLC, so a campaign never blocks it.
  if (hasTaxId && numbersType === 'local') {
    return tiers({ BP: 'recommended', LN: 'optional', TF: 'optional', Calling: 'recommended' })
  }
  if (!hasTaxId && numbersType === 'local') {
    return tiers({ BP: 'optional', LN: 'optional', TF: 'optional', Calling: 'optional' })
  }
  return TOLL_FREE_FIRST
}

export const deriveComplianceTiers = (input: IComplianceTierInput): IComplianceTiers => {
  const tiers = base(input)
  // An unverified toll-free number is unusable, so it stops being optional.
  if (input.hasUnverifiedTollFree && tiers.TF !== 'required') return { ...tiers, TF: 'required' }
  return tiers
}
